import { elevate } from './voyado-esales-api.js';

/**
 * Elevate (Voyado) namespace: `api`, session-ish getters, helpers.
 *
 * `window.elevate.api` is a getter that always returns the current live Elevate
 * client. The client is created on module load and recreated when tracking
 * consent is granted, so the SDK does not keep a stale session snapshot from
 * before consent was reflected in `BAO.utils`. Always reach through the full
 * `window.elevate.api.query…` / `notify…` chain at call time; never cache the
 * `api` reference in a long-lived variable.
 *
 * Use `onTrackingConsentGranted` (or `trackingConsentGranted`) before notify/query
 * work that must respect Shopify tracking consent.
 * `onTrackingConsentLoaded` runs once the consent-tracking-api feature has loaded,
 * regardless of whether consent is granted; the callback receives the resolved boolean.
 * `predictedConsent` synchronously guesses consent on first paint by checking the
 * persisted Elevate customer key cookie (set only after consent has previously been granted),
 * letting callers branch before the consent-tracking feature finishes loading.
 * `onApiReady` runs when `api` is the live client (effectively immediately after load).
 * Use `isLiveElevateApi(window.elevate.api)` when distinguishing the real client.
 */

/**
 * Production Cluster ID: w42ABA460
 * Development Cluster ID: w2DBBC576
 */

const CONSENT_TRACKING_FEATURE = {
  name: 'consent-tracking-api',
  version: '0.1',
};

/**
 * Mirrors `CUSTOMER_KEY_STORAGE` in `bao-utils.js`. `bao-utils` only writes a
 * non-anon (`cust_*` / `BAO.customer_id`) value to the customer key cookie when
 * consent is granted; denied path clears it, unknown path never writes. So a
 * stored value is a reliable signal that consent was granted in a prior session.
 */
const PREDICTED_CONSENT_STORAGE_KEY = 'elevate_customer_key';

/** Real Elevate clients returned from `elevate()` are registered here. */
const liveElevateApis = new WeakSet();

/** The current live Elevate client; swapped out when consented keys change. */
let currentElevateClient = null;

window.elevate = window.elevate || {};

/** Mirrors country groups in `snippets/voyado-market.liquid` / `bao-utils.js`. */
const SUPPORTED_MARKETS = new Set(['AUSNZ', 'EUR', 'USCAN', 'DE', 'GB', 'ROW']);

function getElevateMarket() {
  try {
    return window.BAO?.utils?.getCountryGroup?.(Shopify.country) || '';
  } catch {
    return '';
  }
}

/** Mirrors `voyado_supported` from `snippets/voyado-market.liquid`. */
function isSupportedMarket() {
  return SUPPORTED_MARKETS.has(getElevateMarket());
}

function getElevateLocale() {
  const market = getElevateMarket();

  switch (market) {
    case 'USCAN':
      return 'en-US';
    case 'AUSNZ':
      return 'en-AU';
    case 'DE': {
      const language = String(window.Shopify?.locale || '').toLowerCase().split('-')[0];
      return language === 'de' ? 'de-DE' : 'en-GB';
    }
    case 'EUR':
      return 'en-150';
    default:
      return 'en-GB';
  }
}

function getElevateTouchpoint() {
  return window.innerWidth < 767 ? 'mobile' : 'desktop';
}

/**
 * Once tracking consent is granted we re-fetch from BAO.utils (which now
 * passes its own consent check and returns the persisted real keys) and store
 * them here, so every subsequent read returns the same stable real value.
 */
let consentedCustomerKey = null;
let consentedSessionKey = null;

function getElevateCustomerKey() {
  return consentedCustomerKey
    || window.elevate.__earlyCustomerKey
    || window.BAO?.utils?.getCustomerKey?.()
}

function getElevateSessionKey() {
  return consentedSessionKey
    || window.elevate.__earlySessionKey
    || window.BAO?.utils?.getSessionKey?.()
}

function refreshConsentedKeys() {
  if (!isTrackingConsentGranted()) return;

  consentedCustomerKey = window.BAO?.utils?.getCustomerKey?.() || consentedCustomerKey;
  consentedSessionKey = window.BAO?.utils?.getSessionKey?.() || consentedSessionKey;
}

function isTrackingConsentGranted() {
  try {
    const value = window.Shopify?.customerPrivacy?.getTrackingConsent?.();

    return value === true || value === 'yes';
  } catch {
    return false;
  }
}

/**
 * Synchronous best-guess of tracking consent before the consent-tracking-api
 * feature has loaded. `true` = a non-anon Elevate customer key cookie is persisted
 * (consent was granted previously); `false` = anon / no key stored.
 */
function getPredictedConsent() {
  try {
    return Boolean(window.BAO?.utils?.getCookie?.(PREDICTED_CONSENT_STORAGE_KEY));
  } catch {
    return false;
  }
}

Object.defineProperties(window.elevate, {
  market: { enumerable: true, configurable: true, get: getElevateMarket },
  locale: { enumerable: true, configurable: true, get: getElevateLocale },
  touchpoint: { enumerable: true, configurable: true, get: getElevateTouchpoint },
  customerKey: { enumerable: true, configurable: true, get: getElevateCustomerKey },
  sessionKey: { enumerable: true, configurable: true, get: getElevateSessionKey },
  trackingConsentGranted: { enumerable: true, configurable: true, get: isTrackingConsentGranted },
  predictedConsent: { enumerable: true, configurable: true, get: getPredictedConsent },
  supported: { enumerable: true, configurable: true, get: isSupportedMarket },
});

/** @type {Set<(api: unknown) => void>} */
const apiReadySubscribers = new Set();

/** @type {Set<(api: unknown) => void>} */
const trackingConsentGrantedSubscribers = new Set();

/** @type {Set<(granted: boolean) => void>} */
const trackingConsentLoadedSubscribers = new Set();

let trackingConsentLoaded = false;

function isLiveElevateApi(api) {
  return api != null && liveElevateApis.has(api);
}

window.elevate.isLiveElevateApi = isLiveElevateApi;

function loadConsentTrackingApi() {
  return new Promise((resolve, reject) => {
    const loadFeatures = window.Shopify?.loadFeatures;

    if (typeof loadFeatures !== 'function') {
      reject(new Error('Shopify.loadFeatures is not available'));
      return;
    }

    loadFeatures([CONSENT_TRACKING_FEATURE], (error) => {
      if (error) reject(error);
      else resolve();
    });
  });
}

function createElevateClient() {
  const customerKey = getElevateCustomerKey();
  const sessionKey = getElevateSessionKey();

  console.log('[ELEVATE] Creating client', { customerKey, sessionKey });

  const client = elevate({
    market: getElevateMarket(),
    locale: getElevateLocale(),
    clusterId: 'w42ABA460',
    touchpoint: getElevateTouchpoint(),
    session: () => ({
      customerKey: getElevateCustomerKey(),
      sessionKey: getElevateSessionKey(),
    }),
  });

  liveElevateApis.add(client);
  return client;
}

/**
 * Swap in a fresh Elevate client so subsequent requests use the latest
 * consented keys. No-op until tracking consent has been granted.
 */
function replaceElevateClient() {
  if (!isSupportedMarket() || !isTrackingConsentGranted()) return;

  currentElevateClient = createElevateClient();
  console.log('[ELEVATE] Client replaced (consent/keys sync)');
}

function dispatchApiReady() {
  const api = window.elevate.api;

  if (!isLiveElevateApi(api)) return;

  apiReadySubscribers.forEach((callback) => {
    try {
      callback(api);
    } catch {
      /* ignore subscriber errors */
    }
  });
  apiReadySubscribers.clear();
}

function dispatchTrackingConsentGranted() {
  if (!isTrackingConsentGranted()) return;

  const api = window.elevate.api;

  if (!isLiveElevateApi(api)) return;

  trackingConsentGrantedSubscribers.forEach((callback) => {
    try {
      callback(api);
    } catch {
      /* ignore subscriber errors */
    }
  });
  trackingConsentGrantedSubscribers.clear();
}

function dispatchTrackingConsentLoaded() {
  const granted = isTrackingConsentGranted();

  trackingConsentLoadedSubscribers.forEach((callback) => {
    try {
      callback(granted);
    } catch {
      /* ignore subscriber errors */
    }
  });
  trackingConsentLoadedSubscribers.clear();
}

/**
 * Runs when the live Elevate `api` is assigned (typically immediately on load).
 *
 * @param {(api: unknown) => void} callback
 */
window.elevate.onApiReady = (callback) => {
  if (typeof callback !== 'function') return;

  if (isLiveElevateApi(window.elevate.api)) {
    queueMicrotask(() => callback(window.elevate.api));
    return;
  }

  apiReadySubscribers.add(callback);
};

const queuedApiReady = window.elevate.__apiReadyQueue;
if (Array.isArray(queuedApiReady)) {
  queuedApiReady.splice(0).forEach((callback) => {
    if (typeof callback === 'function') apiReadySubscribers.add(callback);
  });
}

/**
 * Runs once Shopify tracking consent is granted; use for notify/query that require consent.
 * Receives the same live `api` as `onApiReady`.
 *
 * @param {(api: unknown) => void} callback
 */
window.elevate.onTrackingConsentGranted = (callback) => {
  if (typeof callback !== 'function') return;

  if (isTrackingConsentGranted() && isLiveElevateApi(window.elevate.api)) {
    queueMicrotask(() => callback(window.elevate.api));
    return;
  }

  trackingConsentGrantedSubscribers.add(callback);
};

/**
 * Runs once the Shopify consent-tracking-api feature has finished loading,
 * regardless of whether consent was granted. The callback receives the
 * resolved boolean state, so callers can branch on granted vs. denied.
 *
 * @param {(granted: boolean) => void} callback
 */
window.elevate.onTrackingConsentLoaded = (callback) => {
  console.log("[ELEVATE] onTrackingConsentLoaded")
  if (typeof callback !== 'function') return;

  if (trackingConsentLoaded) {
    console.log("[ELEVATE] onTrackingConsentLoaded: trackingConsentLoaded")
    const granted = isTrackingConsentGranted();
    queueMicrotask(() => callback(granted));
    return;
  }

  trackingConsentLoadedSubscribers.add(callback);
};

function onVisitorConsentCollected() {
  refreshConsentedKeys();
  replaceElevateClient();
  dispatchTrackingConsentGranted();
}

async function bootstrap() {
  if (!isSupportedMarket()) return;

  try {
    await loadConsentTrackingApi();
  } catch {
    return;
  }

  trackingConsentLoaded = true;
  dispatchTrackingConsentLoaded();
  refreshConsentedKeys();
  replaceElevateClient();
  dispatchTrackingConsentGranted();
  document.addEventListener('visitorConsentCollected', onVisitorConsentCollected);
}

Object.defineProperty(window.elevate, 'api', {
  enumerable: true,
  configurable: true,
  get: () => currentElevateClient,
});

function getCollectionPageReference() {
  const path = window.location.pathname;
  if (!/\/collections\//.test(path) || /\/products\//.test(path)) return '';

  return path.replace(/^\/[a-zA-Z-]+\/collections\/|^\/collections\/|\/$/g, '');
}

function shouldPrefetchCollectionLandingPage() {
  if (!getCollectionPageReference()) return false;

  const params = new URLSearchParams(window.location.search);
  if (params.has('q') || params.has('sort')) return false;

  for (const key of params.keys()) {
    if (key.startsWith('filter.')) return false;
  }

  return true;
}

function prefetchCollectionLandingPage() {
  if (window.elevate.pendingLandingPage) return;
  if (!currentElevateClient || !shouldPrefetchCollectionLandingPage()) return;

  const pageReference = `/${getCollectionPageReference()}`;
  const requestKey = `${pageReference}|RELEVANCE|0`;

  window.elevate.pendingLandingPage = {
    key: requestKey,
    promise: currentElevateClient.query.landingPage({
      pageReference,
      sort: 'RELEVANCE',
      facets: {},
      limit: 48,
      skip: 0,
      notify: getPredictedConsent(),
    }),
  };
}

if (isSupportedMarket()) {
  currentElevateClient = createElevateClient();
  prefetchCollectionLandingPage();
  dispatchApiReady();
  void bootstrap();
}